import { useState } from 'react';
import { todayIn } from '@njpos/core';
import { api, usd } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';

type Period = 'day' | 'week' | 'month' | 'range';
const label = (c: string) => c.replace('_', ' ').replace(/^./, (x) => x.toUpperCase());
const fmt = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
const change = (now: number, before: number) => (before ? `${now >= before ? '+' : '−'}${Math.abs(Math.round(((now - before) / before) * 100))}% vs the period before` : '');

// Sales for a day, a week (Sunday to Saturday) or a month, with sales tax and each category's share.
export default function SalesSummary() {
  const [period, setPeriod] = useState<Period>('day');
  const [date, setDate] = useState(todayIn());
  const [from, setFrom] = useState(todayIn());
  const [to, setTo] = useState(todayIn());
  const q = period === 'range' ? `period=range&from=${from}&to=${to}` : `period=${period}&date=${date}`;
  const r = useLoad(() => api<any>(`/api/sales-summary?${q}`), [q]);
  const d = r.data;
  const t = d?.totals;
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Sales summary</h1><p>What you sold, the sales tax collected, and how much came from each category.</p></div>
        <button onClick={() => window.print()}>Print</button>
      </div>
      <div className="row wrap" style={{ gap: 8 }}>
        {(['day', 'week', 'month', 'range'] as Period[]).map((p) => <button key={p} className={period === p ? 'primary' : ''} onClick={() => setPeriod(p)}>{({ day: 'Day', week: 'Week', month: 'Month', range: 'Custom' })[p]}</button>)}
        {period !== 'range'
          ? <label>Pick a date in it<input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} /></label>
          : <><label>From<input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label><label>To<input type="date" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} /></label></>}
      </div>
      <ErrorNote error={r.error} />
      {d && (
        <>
          <h2 style={{ margin: 0 }}>{d.from === d.to ? fmt(d.from) : `${fmt(d.from)} to ${fmt(d.to)}`}</h2>
          <div className="stats">
            <div><div className="v">{usd(t.net)}</div><div className="l">Net sales ({t.sales} sales)</div><div className="faint">{change(t.net, d.previous.net_cents)}</div></div>
            <div><div className="v">{usd(t.tax)}</div><div className="l">Sales tax collected</div></div>
            <div><div className="v">{usd(t.total)}</div><div className="l">Total taken in</div></div>
            <div><div className="v">{usd(t.margin_cents)}</div><div className="l">Gross profit</div></div>
          </div>
          <div className="faint">Before discounts {usd(t.gross)} · discounts {usd(t.discounts)} · refunds {t.refunds} · average sale {usd(t.avg_sale_cents)}{t.rounding ? ` · cash rounding ${usd(t.rounding)}` : ''}</div>

          <div className="panel"><div className="panel-head"><h2>By category</h2></div>
            <div className="table-wrap"><table className="grid">
              <thead><tr><th>Category</th><th className="n">Units</th><th className="n">Sales</th><th className="n">Sales tax</th><th className="n">Total</th><th className="n">Share</th></tr></thead>
              <tbody>
                {d.categories.map((c: any) => (
                  <tr key={c.category}><td>{label(c.category)}</td><td className="n">{c.units}</td><td className="n">{usd(c.net_cents)}</td><td className="n">{usd(c.tax_cents)}</td><td className="n">{usd(c.total_cents)}</td>
                    <td className="n">{c.share_pct}%</td></tr>))}
                {d.otherNetCents !== 0 && <tr><td>Other (not by category)</td><td /><td className="n">{usd(d.otherNetCents)}</td><td /><td /><td /></tr>}
                <tr style={{ fontWeight: 700 }}><td>All sales</td><td className="n">{d.categories.reduce((s: number, c: any) => s + c.units, 0)}</td><td className="n">{usd(t.net)}</td><td className="n">{usd(t.tax)}</td><td className="n">{usd(t.net + t.tax)}</td><td /></tr>
              </tbody>
            </table>{d.categories.length === 0 && <div className="empty">No sales in this period.</div>}</div>
          </div>

          <div className="grid-2" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {d.days.length > 1 && (
              <div className="panel"><div className="panel-head"><h2>By day</h2></div>
                <div className="table-wrap"><table className="grid">
                  <thead><tr><th>Day</th><th className="n">Sales</th><th className="n">Net</th><th className="n">Tax</th><th className="n">Total</th></tr></thead>
                  <tbody>{d.days.map((x: any) => <tr key={x.date}><td>{fmt(x.date)}</td><td className="n">{x.sales}</td><td className="n">{usd(x.net_cents)}</td><td className="n">{usd(x.tax_cents)}</td><td className="n">{usd(x.total_cents)}</td></tr>)}</tbody>
                </table></div>
              </div>
            )}
            <div className="panel"><div className="panel-head"><h2>How customers paid</h2></div>
              <table className="grid"><tbody>
                {d.tenders.map((x: any) => <tr key={x.method}><td>{label(x.method)}</td><td className="n">{usd(x.cents)}</td></tr>)}
                {(d.lottery.sales > 0 || d.lottery.payouts > 0) && <>
                  <tr><td>Lottery sales (owed to the state)</td><td className="n">{usd(d.lottery.sales)}</td></tr>
                  <tr><td>Lottery paid out</td><td className="n">{usd(-d.lottery.payouts)}</td></tr></>}
              </tbody></table>
            </div>
          </div>
          <p className="faint">Sales and tax are by the day the sale was rung up, in your store's time zone. Lottery and gift card sales are not part of net sales or sales tax.</p>
        </>
      )}
    </div>
  );
}
